import { deepEqual } from 'fast-equals'
import { snapshot, subscribe } from 'valtio'
import {
  type BaseToolProfile,
  type ProfileId,
  type Tool,
  type ToolProfile,
  type ToolProfiles,
  PROFILE_IDS,
} from '@shared/types'
import type { BannerStore } from '~/stores/banner-store'
import type { OfferwallStore } from '~/stores/offerwall-store'
import type { PaywallStore } from '~/stores/paywall-store'
import type { WidgetStore } from '~/stores/widget-store'
import { diffProfile, type ChangedFields } from '~/utils/profile-diff'
import { omit } from '~/utils/utils.storage'

type Store = BannerStore | WidgetStore | OfferwallStore | PaywallStore
const STORAGE_PREFIX = 'wmt'

export function getStorageKeys(tool: Tool) {
  return {
    snapshotsStorageKey: `${STORAGE_PREFIX}-${tool}-snapshots`,
    getProfileStorageKey: (profileId: ProfileId) =>
      `${STORAGE_PREFIX}-${tool}-${profileId}`,
  }
}

interface ToolStoreConfig<T extends Tool> {
  tool: T
  store: Store
  snapshots: Map<ProfileId, ToolProfile<T>>
}

export function createToolStoreUtils<T extends Tool>(
  config: ToolStoreConfig<T>,
) {
  const { tool, store, snapshots } = config
  const { snapshotsStorageKey, getProfileStorageKey } = getStorageKeys(tool)

  function parseProfileFromStorage(
    profileId: ProfileId,
  ): ToolProfile<T> | null {
    const storageKey = getProfileStorageKey(profileId)
    const storage = localStorage.getItem(storageKey)
    if (!storage) return null

    try {
      const profile: ToolProfile<T> = JSON.parse(storage)
      const isValid =
        typeof profile === 'object' &&
        Object.keys(profile).every((key) => key in store.profile)

      if (!isValid) throw new Error('Invalid profile shape')
      return profile
    } catch (error) {
      console.warn(
        `Failed to load profile ${profileId} from localStorage:`,
        error,
      )
      localStorage.removeItem(storageKey)
      return null
    }
  }

  function subscribeProfileToStorage(profileId: ProfileId) {
    const profile = store.profiles[profileId]
    return subscribe(profile, () => {
      const snap = snapshot(profile)
      localStorage.setItem(
        getProfileStorageKey(profileId),
        JSON.stringify(snap),
      )
    })
  }

  function hasPendingUpdates(id: ProfileId, snap: ToolProfile<T>): boolean {
    const baseline = snapshots.get(id)
    if (!baseline) {
      return false
    }

    const keys: (keyof BaseToolProfile)[] = ['$version', '$modifiedAt']
    return !deepEqual(omit(snap, keys), omit(baseline, keys))
  }

  function subscribeProfileToUpdates(id: ProfileId) {
    const profile = store.profiles[id]
    return subscribe(profile, () => {
      const snap = snapshot(profile) as ToolProfile<T>
      if (hasPendingUpdates(id, snap)) {
        store.profilesUpdate.add(id)
      } else {
        store.profilesUpdate.delete(id)
      }
    })
  }

  function persistSnapshots() {
    localStorage.setItem(
      snapshotsStorageKey,
      JSON.stringify(Object.fromEntries(snapshots)),
    )
  }

  return {
    subscribeProfilesToStorage() {
      const unsubscribes = PROFILE_IDS.map(subscribeProfileToStorage)
      return () => unsubscribes.forEach((s) => s())
    },

    hydrateProfilesFromStorage() {
      PROFILE_IDS.forEach((profileId) => {
        const parsed = parseProfileFromStorage(profileId)
        if (parsed) {
          Object.assign(store.profiles[profileId], parsed)
        }
      })
    },

    captureSnapshotsToStorage() {
      const snap = snapshot(store.profiles)
      Object.entries(snap).forEach(([profileId, profile]) => {
        snapshots.set(profileId as ProfileId, profile as ToolProfile<T>)
      })
      localStorage.setItem(snapshotsStorageKey, JSON.stringify(snap))
    },

    hydrateSnapshotsFromStorage() {
      const storage = localStorage.getItem(snapshotsStorageKey)
      if (!storage) return

      try {
        const stored: ToolProfiles<T> = JSON.parse(storage)
        if (!stored) return

        const isValid = (profile: ToolProfile<T>) =>
          typeof profile === 'object' &&
          Object.keys(profile).every((key) => key in store.profile)

        Object.entries(stored).forEach(([id, profile]) => {
          if (isValid(profile as ToolProfile<T>)) {
            snapshots.set(id as ProfileId, profile as ToolProfile<T>)
          }
        })
      } catch (error) {
        console.warn(`Failed to hydrate ${tool} baselines:`, error)
        localStorage.removeItem(snapshotsStorageKey)
      }
    },

    subscribeProfilesToUpdates() {
      const unsubscribes = PROFILE_IDS.map(subscribeProfileToUpdates)
      return () => unsubscribes.forEach((s) => s())
    },

    // User-initiated save; returns diff for analytics
    commitActiveProfile(activeTab: ProfileId): ChangedFields {
      const prev = snapshots.get(activeTab)!
      const current = snapshot(store.profiles[activeTab]) as ToolProfile<T>
      const changed = diffProfile(prev, current)

      snapshots.set(activeTab, current)
      store.profilesUpdate.delete(activeTab)
      // TODO: determine if this needed here
      persistSnapshots()

      return changed
    },

    // Server-load baseline reset; no analytics
    commitAllProfiles(): void {
      PROFILE_IDS.forEach((id) => {
        const profile = snapshot(store.profiles[id]) as ToolProfile<T>
        snapshots.set(id, profile)
        store.profilesUpdate.delete(id)
      })
      persistSnapshots()
    },

    removeProfilesFromStorage() {
      localStorage.removeItem(snapshotsStorageKey)

      PROFILE_IDS.forEach((id) => {
        const storageKey = getProfileStorageKey(id)
        localStorage.removeItem(storageKey)
      })
    },
  }
}
